// src/rateSheetExtractor.js
// ─────────────────────────────────────────────────────────────
// Reverse-mortgage rate-sheet PDF → structured JSON via Claude.
//
// Design notes:
//   • The app's existing rate-sheet schema uses product-specific string
//     encodings (e.g. margins = "3.000:1500/2.875:1750/..."). Different
//     products have different shapes — ARMs have margins-by-PLU-tier,
//     fixed products have rate-tier pairs, HomeSafe has utilization
//     premiums. The extractor normalizes everything into a single rich
//     JSON object and leaves the final encoding decision to the UI layer.
//   • Format varies by lender (FOA today, MoO + others tomorrow). The
//     prompt is deliberately lender-agnostic: it describes the data
//     shapes, not the layouts.
//   • Output is STRICT JSON — no markdown fences, no prose. We tell
//     Claude this explicitly and parse defensively.
// ─────────────────────────────────────────────────────────────

const EXTRACTION_PROMPT = `You are extracting reverse mortgage rate sheet data from a PDF.

CRITICAL: Return ONLY valid JSON. No markdown code fences, no prose, no explanations. Just the JSON object.

Extract ALL reverse mortgage products from this rate sheet into this schema:

{
  "lender": "FOA" | "MoO" | "Longbridge" | "LiveWell" | "Mutual" | "SmartFi" | "OtherLender",
  "effective_date": "MM/DD/YYYY",            // rate sheet effective date
  "lock_expiration": "MM/DD/YYYY" | null,
  "index_1yr_cmt": "3.690",                  // 1-year CMT as decimal %, no "%"
  "index_10yr": "4.290",                     // 10-year CMT or swap rate
  "products": [
    {
      "product_name": "HECM Monthly Adjustable",  // human-readable name from sheet
      "category": "hecm_arm" | "hecm_fixed" | "hecm_annual_arm" | "hs_standard" | "hs_intro" | "hs_select" | "hs_select_intro" | "hs_second" | "moo_se_fixed_plus" | "moo_se_fixed" | "moo_se_arm" | "moo_hecm_adj" | "smartfi_choice_flex" | "smartfi_choice_flex_service" | "smartfi_choice_fixed_saver" | "smartfi_choice_fixed_max" | "smartfi_choice_fixed_pro" | "smartfi_choice_fixed_core" | "smartfi_choice_fixed_service_saver" | "smartfi_choice_fixed_service_max" | "smartfi_choice_fixed_service_pro" | "smartfi_choice_fixed_service_core" | "lb_platinum_fixed" | "lb_platinum_loc" | "other",
      "life_cap": "5" | "10" | null,         // for ARM products; null if not applicable
      "plu_band_edges": [10, 20, 30, 40, 50, 60, 70, 80, 90, 100] | null,  // UPPER edge (in %) of each utilization/PLU column the premiums_by_plu values sit under, left to right ("0-10%" → 10, "25-60%" → 60, "90.01-100%" → 100). Same length as every premiums_by_plu array. null when the product has no utilization columns.
      "rate_rows": [
        // Each row is one rate/margin tier. Include ALL rows from the table.
        // For ARMs (margin-based products):
        {
          "margin": "3.000",                   // decimal %, no trailing %
          "initial_rate": "6.750",              // if shown
          "expected_rate": "7.250",             // if shown
          "premiums_by_plu": [115.100, 110.760, 108.500, ...]  // array of premiums across PLU% bands, in order from lowest to highest PLU
        }
        // OR for fixed/tier products:
        {
          "rate": "7.930",                      // decimal %
          "lesa_rate": "8.240" | null,          // HomeSafe shows a LESA rate
          "premium": "103.5",                   // decimal, no %
          "orig_fee_dollars": 2000 | null,      // HECM Fixed lists fixed-dollar orig fees
          "orig_fee_pct": "1.0" | null,         // HomeSafe lists orig as % of PL
          "tier_label": "Fixed Tier 1: Low LTV" | null,  // if product has named tiers
          "utilization_tier": "25-80%" | "80-90%" | "90.01-100%" | null  // if product has utilization-based premium
        }
      ],
      "origination_fee_note": "1.0% of Principal Limit Payable to FOA" | "$2,000" | null,
      "loan_min": "200000" | null,
      "loan_max": "4000000" | null,
      "min_comp": "5000" | null,
      "lender_credit": "0" | null,
      "state_age_availability": "Age 55+: AZ, CA, ... Age 62+: TX" | null,
      "special_notes": "First-time reverse only | Excl MN,MO,NY,UT" | null
    }
  ]
}

LENDER DETECTION HINTS:
• "FOA" / "Finance of America" / "FAR" / "FAReverse"  → lender: "FOA"
• "MoO" / "Mutual of Omaha" / "MOM" / "Omaha Mortgage" → lender: "MoO"
• "Longbridge" / "Longbridge Financial" / "LBF"        → lender: "Longbridge"
• "SmartFi" / "Smart Fi" / "Smartfi Reverse"           → lender: "SmartFi"
• Anything else → "OtherLender"

SMARTFI PRODUCT CATEGORIZATION:
SmartFi rate sheets typically include:
• "HECM Monthly Adjustable" or "HECM ARM"  → category: "hecm_arm"
• "HECM Annual Adjustable" or "HECM Annual ARM" → category: "hecm_annual_arm"
• "HECM Fixed"                              → category: "hecm_fixed"
• "Choice Flex" or "Choice Flex ARM" or "Choice LOC" → category: "smartfi_choice_flex"
• "Choice Fixed Saver"                      → category: "smartfi_choice_fixed_saver"
• "Choice Fixed Max"                        → category: "smartfi_choice_fixed_max"
• "Choice Fixed Pro"                        → category: "smartfi_choice_fixed_pro"
• "Choice Fixed Core"                       → category: "smartfi_choice_fixed_core"
IMPORTANT — "Choice Service" tables: SmartFi sheets often carry a SECOND Choice Fixed table and a SECOND Choice Flex table labeled "Choice Fixed Service" / "Choice Flex Service" (noted as "for active service members and veterans"). These are veterans-only rates and must NEVER be mixed into the regular Choice categories above. Emit them as separate products:
• "Choice Fixed Service" row named Saver     → category: "smartfi_choice_fixed_service_saver"
• "Choice Fixed Service" row named Max       → category: "smartfi_choice_fixed_service_max"
• "Choice Fixed Service" row named Pro       → category: "smartfi_choice_fixed_service_pro"
• "Choice Fixed Service" row named Core      → category: "smartfi_choice_fixed_service_core"
• "Choice Flex Service" margin table         → category: "smartfi_choice_flex_service"
SmartFi rate sheets often present margin tables with premium percentages across multiple PLU utilization bands (e.g. "0-10%", "10-20%", … "90-100%"). Capture all premium values in premiums_by_plu in the order shown left-to-right.

LONGBRIDGE PRODUCT CATEGORIZATION:
Longbridge offers both standard HECM and proprietary Platinum products:
• "HECM Monthly Adjustable" / "HECM ARM"   → category: "hecm_arm"
• "HECM Annual Adjustable"                  → category: "hecm_annual_arm"
• "HECM Fixed"                              → category: "hecm_fixed"
• "Platinum Fixed" / "Platinum Mortgage Fixed"   → category: "lb_platinum_fixed"
• "Platinum LOC" / "Platinum ARM" / "Platinum Mortgage LOC" → category: "lb_platinum_loc"
Longbridge Platinum Fixed sheets typically show one or more rate rows with origination percentage. Platinum LOC sheets show margin tiers (similar in shape to FOA HomeSafe Select).

RULES:
1. Do not fabricate rows. If a cell is empty or unreadable, omit that field.
2. Numeric values are strings for rates/margins (preserve precision) and numbers for dollar amounts.
3. For products with multiple utilization tiers on the same margin row (HomeSafe Select, SmartFi Choice Flex, Longbridge Platinum LOC, MoO SecureEquity ARM), create ONE row per margin with premiums_by_plu mapped across EVERY utilization column shown — never just the first — and put the column headers' upper edges in plu_band_edges.
4. For HECM Monthly Adjustable tables, premiums_by_plu is typically an array of 10 values across the PLU% bands (plu_band_edges 10,20,…,100). premiums_by_plu values are numbers.
4b. For fixed products that list an origination % per rate (SecureEquity Fixed Plus, HomeSafe Standard/Intro, Platinum Fixed), put it in orig_fee_pct on that rate's row.
5. If you cannot confidently identify a product category from the fixed list above, use "other" and fill product_name accurately.
6. Extract EVERY product on every page. Multi-product PDFs are common — SmartFi and Longbridge sheets often have 5+ products spread across multiple pages.
7. Return valid JSON only — no \`\`\`json fences.`;

// Parse Claude's response — be defensive against accidental markdown wrapping.
function parseExtraction(text) {
  if (!text) return null;
  let cleaned = String(text).trim();
  // Strip any accidental markdown code fence
  const fenceMatch = cleaned.match(/```(?:json)?\s*([\s\S]+?)```/);
  if (fenceMatch) cleaned = fenceMatch[1].trim();
  // Strip leading/trailing non-JSON chatter
  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    cleaned = cleaned.slice(firstBrace, lastBrace + 1);
  }
  try {
    return JSON.parse(cleaned);
  } catch (e) {
    console.warn("Rate sheet extraction JSON parse failed:", e.message, cleaned.slice(0, 200));
    return null;
  }
}

// The PDF goes to /api/anthropic-proxy inline as base64 JSON. Vercel rejects
// a function request body over ~4.5MB, and base64 inflates the file by a
// third, so anything much past 3MB fails with a 413 after the upload.
export const MAX_RATE_SHEET_BYTES = 3 * 1024 * 1024;
export const MAX_RATE_SHEET_LABEL = "3MB";

// Main entry point. Accepts a data-URL (data:application/pdf;base64,xxx) and
// the caller's Supabase admin access token (used to authenticate with the
// /api/anthropic-proxy serverless route — the Anthropic key never reaches
// the browser). Returns the parsed extraction object or { error }.
export async function extractRateSheet(dataUrl, accessToken) {
  if (!accessToken) {
    return { error: "Sign in as admin to extract rate sheets." };
  }
  if (!dataUrl || !dataUrl.startsWith("data:application/pdf")) {
    return { error: "Please upload a PDF file." };
  }
  const base64 = dataUrl.split(",")[1];
  if (!base64) return { error: "Could not read PDF file." };
  if (base64.length * 0.75 > MAX_RATE_SHEET_BYTES) {
    return { error: "This PDF is over " + MAX_RATE_SHEET_LABEL + ", which is more than the extractor can send. Save just the rate pages as a smaller PDF and try again." };
  }

  try {
    const resp = await fetch("/api/anthropic-proxy", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + accessToken,
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        // 8000 leaves enough headroom for SmartFi and Longbridge sheets that
        // pack 6-10 products with multi-tier margin tables across multiple
        // pages. Capped at the proxy's MAX_TOKENS_CAP.
        max_tokens: 8000,
        messages: [{
          role: "user",
          content: [
            { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
            { type: "text", text: EXTRACTION_PROMPT },
          ],
        }],
      }),
    });
    if (!resp.ok) {
      const body = await resp.text();
      return { error: "Rate sheet extraction failed (" + resp.status + "): " + body.slice(0, 200) };
    }
    const data = await resp.json();
    const text = data.content && data.content[0] ? data.content[0].text : "";
    const parsed = parseExtraction(text);
    if (!parsed) return { error: "Could not parse rate sheet. Claude returned an unreadable response." };
    if (!Array.isArray(parsed.products) || parsed.products.length === 0) {
      return { error: "No rate sheet products were detected in this PDF." };
    }
    return { ok: true, extraction: parsed };
  } catch (e) {
    return { error: "Network error: " + (e && e.message ? e.message : String(e)) };
  }
}

// ─────────────────────────────────────────────────────────────
// Mapping from the extractor's `category` enum → the app's existing
// rateSheets key. If a category maps to null, it's a new/unrecognized
// product the user will hand-wire (out of V1 scope).
// ─────────────────────────────────────────────────────────────
export function mapCategoryToAppKey(category, lender, lifeCap) {
  const l = (lender || "").toLowerCase();
  const isFoa = l.includes("foa") || l.includes("finance of america") || l.includes("far ");
  const isMoo = l.includes("moo") || l.includes("mutual of omaha");
  const isSmartfi = l.includes("smartfi") || l.includes("smart fi");
  const isLongbridge = l.includes("longbridge") || l.includes("lbf");
  switch (category) {
    case "hecm_arm":
      if (isFoa && lifeCap !== "10") return "foaHecmAdj";
      if (isMoo) return "mooHecmAdj";
      if (isSmartfi) return "smartHecmAdj";
      if (isLongbridge) return "lbHecmAdj";
      return null;
    case "hecm_annual_arm":
      if (isSmartfi) return "smartHecmAdjAnnual";
      if (isLongbridge) return "lbHecmAdjAnnual";
      return null;
    case "hecm_fixed":
      if (isFoa) return "foaHecmFixed";
      if (isSmartfi) return "smartHecmFixed";
      if (isLongbridge) return "lbHecmFixed";
      return null;
    case "hs_standard":    return isFoa ? "foaHSStd" : null;
    case "hs_intro":       return isFoa ? "foaHSIntro" : null;
    case "hs_select":      return null; // original HomeSafe Select is retired (no new apps since 6/5/26) — not priced, not stored
    case "hs_select_intro":return isFoa ? "foaHSSelIntro" : null;
    case "hs_second":      return isFoa ? "foaHS2" : null;
    case "moo_se_fixed_plus": return "mooSEFixedPlus";
    case "moo_se_fixed":   return "mooSEFixed";
    case "moo_se_arm":     return "mooSEARM";
    case "moo_hecm_adj":   return "mooHecmAdj";
    case "smartfi_choice_flex":         return "smartChoiceFlex";
    case "smartfi_choice_fixed_saver":  return "smartChoiceFixedSaver";
    case "smartfi_choice_fixed_max":    return "smartChoiceFixedMax";
    case "smartfi_choice_fixed_pro":    return "smartChoiceFixedPro";
    case "smartfi_choice_fixed_core":   return "smartChoiceFixedCore";
    // Choice Service (veterans/active service) maps to the SAME app entry as
    // its regular counterpart — encodeForAppKey routes it into the svcTiers/
    // svcMargins field off product.category, so it never clobbers the
    // civilian tiers.
    case "smartfi_choice_flex_service":       return "smartChoiceFlex";
    case "smartfi_choice_fixed_service_saver":return "smartChoiceFixedSaver";
    case "smartfi_choice_fixed_service_max":  return "smartChoiceFixedMax";
    case "smartfi_choice_fixed_service_pro":  return "smartChoiceFixedPro";
    case "smartfi_choice_fixed_service_core": return "smartChoiceFixedCore";
    case "lb_platinum_fixed":  return "lbPlatinumFixed";
    case "lb_platinum_loc":    return "lbPlatinumLOC";
    default: return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Encode a product's extracted rate_rows into the app's string format.
// The format varies per appKey because the calculator parses each
// product's `margins` / `tiers` string differently. This function is the
// ONLY place that needs to know those encodings, and it must write exactly
// what Calc reads (premAt in Calc.jsx):
//   • Banded ARMs (every HECM ARM, Choice Flex, HomeSafe Select / Select
//     Intro, SecureEquity ARM, Platinum LOC):
//       "margin:p1,p2,…,pn/margin:p1,…"  + `bands` = "edge1,…,edgen"
//     One premium PER PLU BAND. Keeping only the first band (the old
//     behaviour) priced every draw at the 0-10% premium and overstated comp
//     by tens of thousands; dropping the price entirely zeroed it.
//   • rate:orig% fixed (HomeSafe Std / Intro, Platinum Fixed, SecureEquity
//     Fixed Plus) — the orig % decides the borrower's cost per tier.
//   • rate:origFee$ (HECM Fixed — not priced, kept for the editor).
//   • bare rates (HomeSafe 2nd, SecureEquity Fixed, SmartFi fixed).
// ─────────────────────────────────────────────────────────────
const BANDED_KEYS = ["foaHecmAdj", "mooHecmAdj", "smartHecmAdj", "smartHecmAdjAnnual", "lbHecmAdj", "lbHecmAdjAnnual", "smartChoiceFlex", "foaHSSelect", "foaHSSelIntro", "mooSEARM", "lbPlatinumLOC"];
const RATE_ORIG_PCT_KEYS = ["foaHSStd", "foaHSIntro", "lbPlatinumFixed", "mooSEFixedPlus"];
const RATE_ORIG_FEE_KEYS = ["foaHecmFixed", "lbHecmFixed"];
const BARE_RATE_KEYS = ["foaHS2", "mooSEFixed", "smartHecmFixed", "smartChoiceFixedSaver", "smartChoiceFixedMax", "smartChoiceFixedPro", "smartChoiceFixedCore"];

const blank = (v) => v === undefined || v === null || String(v).trim() === "";
const pick = (v) => blank(v) ? "" : String(v).trim();
// Margins and premiums are stored with 3 decimals ("2.750", "114.350").
const fmt3 = (v) => { const n = parseFloat(v); return isNaN(n) ? pick(v) : n.toFixed(3); };
const pricesOf = (r) => {
  if (Array.isArray(r.premiums_by_plu) && r.premiums_by_plu.length) {
    return r.premiums_by_plu.filter((x) => !blank(x) && !isNaN(parseFloat(x))).map(fmt3);
  }
  return blank(r.premium) ? [] : [fmt3(r.premium)];
};
const edgesOf = (product) => {
  const e = product && Array.isArray(product.plu_band_edges) ? product.plu_band_edges : null;
  if (!e || !e.length) return null;
  const nums = e.map((x) => parseFloat(x)).filter((x) => !isNaN(x));
  return nums.length === e.length ? nums.map((x) => String(Math.round(x * 100) / 100)) : null;
};

export function encodeForAppKey(appKey, product) {
  const rows = product && Array.isArray(product.rate_rows) ? product.rate_rows : [];
  const isService = /_service/.test(String((product && product.category) || ""));

  if (BANDED_KEYS.indexOf(appKey) >= 0) {
    const parts = rows.filter((r) => !blank(r.margin)).map((r) => {
      const prices = pricesOf(r);
      return fmt3(r.margin) + (prices.length ? ":" + prices.join(",") : "");
    });
    // Choice Flex SERVICE (veterans-only margins) writes svcMargins, so the
    // civilian margins survive.
    const field = appKey === "smartChoiceFlex" && isService ? "svcMargins" : "margins";
    const data = {};
    data[field] = parts.join("/");
    const edges = edgesOf(product);
    if (edges) data.bands = edges.join(",");
    return { data };
  }
  if (RATE_ORIG_PCT_KEYS.indexOf(appKey) >= 0) {
    const parts = rows.filter((r) => !blank(r.rate)).map((r) => pick(r.rate) + (blank(r.orig_fee_pct) ? "" : ":" + pick(r.orig_fee_pct)));
    return { data: { tiers: parts.join("/") } };
  }
  if (RATE_ORIG_FEE_KEYS.indexOf(appKey) >= 0) {
    const parts = rows.filter((r) => !blank(r.rate)).map((r) => pick(r.rate) + (blank(r.orig_fee_dollars) ? "" : ":" + pick(r.orig_fee_dollars)));
    return { data: { tiers: parts.join("/") } };
  }
  if (BARE_RATE_KEYS.indexOf(appKey) >= 0) {
    // A Choice SERVICE product (veterans-only rates, category carries
    // "_service_") writes svcTiers instead — the civilian tiers survive.
    const parts = rows.filter((r) => !blank(r.rate)).map((r) => pick(r.rate));
    const svc = isService && /^smartChoiceFixed/.test(appKey);
    return { data: svc ? { svcTiers: parts.join("/") } : { tiers: parts.join("/") } };
  }
  return { data: {} };
}

// ─────────────────────────────────────────────────────────────
// Compare / guard an extracted value against what the sheet holds now.
// ─────────────────────────────────────────────────────────────
// Split "m:p1,p2/m:p1" into tiers. `prices` is the comma list after the
// first ':' (an orig % or fee counts as one "price" for the ':' check).
function tiersOf(s) {
  return String(s || "").split("/").map((t) => t.trim()).filter(Boolean).map((t) => {
    const i = t.indexOf(":");
    const head = i >= 0 ? t.slice(0, i) : t;
    const tail = i >= 0 ? t.slice(i + 1) : "";
    return { head: head.trim(), tail: tail.trim(), prices: tail.split(",").map((x) => x.trim()).filter(Boolean) };
  });
}
// "2.750" and "2.75" are the same number; anything that isn't a plain number
// ("broker", "Full") compares as text.
const normNum = (x) => { const s = String(x).trim(); return /^-?\d*\.?\d+$/.test(s) ? String(parseFloat(s)) : s; };
// Numeric-aware form: "2.750:114.350" and "2.75:114.35" are the same value, so
// an unchanged row is never flagged "Changed" (or pre-checked) over formatting.
export function normalizeEncoded(s) {
  return tiersOf(s).map((t) => normNum(t.head) + (t.tail ? ":" + t.prices.map(normNum).join(",") : "")).join("/");
}
export function sameEncoded(a, b) {
  return normalizeEncoded(a) === normalizeEncoded(b);
}
const countEdges = (b) => String(b || "").split(",").map((x) => parseFloat(x)).filter((x) => !isNaN(x)).length;
const sameEdges = (a, b) => String(a || "").split(",").map(normNum).join(",") === String(b || "").split(",").map(normNum).join(",");

// What applying `newValue` over `currentValue` would do, and whether it is
// safe to pre-check. Returns {changed, warnings[], bands} where `bands` is the
// band-edge string to write alongside (or null to leave the sheet's as is).
// Any warning means the row starts UNCHECKED: the value lost information the
// current sheet carries (per-band prices, a margin's price, a tier's orig %),
// or the band headers don't line up with the price columns.
export function assessRateSheetChange(currentValue, newValue, currentBands, newBands) {
  const warnings = [];
  const cur = tiersOf(currentValue);
  const nxt = tiersOf(newValue);
  const changed = !sameEncoded(currentValue, newValue);
  if (!nxt.length) return { changed: changed, warnings: cur.length ? ["Nothing was read for this product — applying would clear it."] : [], bands: null };
  const curHasTail = cur.some((t) => t.tail);
  const curMaxPrices = cur.reduce((m, t) => Math.max(m, t.prices.length), 0);
  if (curHasTail && nxt.some((t) => !t.tail)) warnings.push("Some rows lost their price / orig % (the part after ':') — the calculator would fall back to a default.");
  if (curMaxPrices > 1 && nxt.some((t) => t.tail && t.prices.length < curMaxPrices)) warnings.push("Fewer PLU prices per margin than the current sheet (" + curMaxPrices + " now) — comp would be priced off the wrong band.");
  const newMax = nxt.reduce((m, t) => Math.max(m, t.prices.length), 0);
  if (newMax > 1 && nxt.some((t) => t.tail && t.prices.length !== newMax)) warnings.push("Margins have different numbers of PLU prices — check the New value.");
  let bands = null;
  if (newMax > 1) {
    if (!blank(newBands)) {
      if (countEdges(newBands) !== newMax) warnings.push("The sheet's PLU columns (" + countEdges(newBands) + ") don't match the prices per margin (" + newMax + ").");
      else if (!sameEdges(newBands, currentBands)) bands = String(newBands);
    } else if (countEdges(currentBands) !== newMax) {
      warnings.push("No PLU column headers were read, and the current bands (" + (countEdges(currentBands) || "none") + ") don't match " + newMax + " prices per margin.");
    }
  }
  return { changed: changed || !!bands, warnings: warnings, bands: bands };
}
